// Campaign health (v0.8.9) — the flow-analytics view.
//
// The battlemap answers "what is the state of the promise". This
// answers the questions a snapshot cannot: where effort is going,
// where the bottleneck actually is, whether the campaign is speeding
// up or slowing down, whether the load is balanced, and what will
// still be open when the sprint ends.
//
// It is a separate page rather than another panel on the map because
// they are different instruments. The map is for the team — structure,
// ownership, focus. This is for the operator — rates and pressure.
// Bolting the second onto the first produced a map carrying analyses it
// was never shaped for.
//
// Admin-only, like the Commander view.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';

interface Team { id: string; name: string; slug: string }

type Phase =
  | 'not_started'
  | 'in_progress'
  | 'finishing'
  | 'blocked'
  | 'done'
  | 'other';

interface DwellRow {
  phase: Phase;
  count: number;
  median_days: number;
  oldest_days: number;
  oldest_key: string | null;
}

interface CampaignHealth {
  days: number;
  phases: Record<Phase, number>;
  dwell: DwellRow[];
  flow: {
    wip: number;
    completed: number;
    elapsed_days: number;
    throughput_per_day: number;
    cycle_time_days: number | null;
    cycle_time_median_days: number | null;
    days_remaining: number | null;
    expected_to_finish: number | null;
    expected_carry_over: number | null;
    work_started: number;
    finished_events: number;
    sent_back: number;
    rework_rate: number | null;
    /** Committed, not started. A queue, not a load. */
    queue: number;
  };
  cfd: Array<{ day: number; starts_at: string; counts: Record<Phase, number> }>;
  daily_flow: Array<{ day: number; starts_at: string; arrivals: number; departures: number; sent_back: number; blocked: number }>;
  people: Array<{ name: string; in_progress: number; finishing: number; blocked: number; total: number }>;
  headline: string;
}

interface SprintSummary {
  id: string;
  name: string;
  status: string;
  starts_at: string;
  ends_at: string | null;
  committed: number;
  days: number;
  completed: number;
  throughput_per_day: number;
  cycle_time_median_days: number | null;
  rework_rate: number | null;
  work_started: number;
  sent_back: number;
  wip: number;
}

interface Payload {
  team_id: string;
  adventure: { id: string; name: string; place: string | null; starts_at: string; ends_at: string | null; committed: number } | null;
  health: CampaignHealth | null;
  trend: SprintSummary[];
}

// Colour carries one meaning: the phase. Same palette as the battlemap
// flow panel, so the two read as one system.
const PHASE_COLOUR: Record<Phase, string> = {
  not_started: 'bg-zinc-500/50',
  in_progress: 'bg-sky-400/70',
  finishing: 'bg-amber-400/80',
  blocked: 'bg-rose-500/80',
  done: 'bg-emerald-400/60',
  other: 'bg-zinc-600/40',
};
const PHASE_LABEL: Record<Phase, string> = {
  not_started: 'Queued',
  in_progress: 'In progress',
  finishing: 'Finishing',
  blocked: 'Blocked',
  done: 'Done',
  other: 'Unknown',
};
const CFD_ORDER: Phase[] = ['done', 'finishing', 'in_progress', 'blocked', 'not_started', 'other'];

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-zinc-100">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-zinc-400">{sub}</div> : null}
    </div>
  );
}

export function CampaignHealthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useQuery<{ teams: Team[] }>({ queryKey: ['teams'], queryFn: () => api<{ teams: Team[] }>('/api/teams') });
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';

  const { data, isLoading, error } = useQuery<Payload>({
    queryKey: ['campaign-health', teamId],
    queryFn: () => api<Payload>(`/api/admin/campaign-health?team_id=${encodeURIComponent(teamId)}`),
    enabled: !!teamId,
    refetchInterval: 60_000,
  });

  if (isLoading) return <div className="p-6 text-sm text-zinc-400">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-rose-300">Failed to load campaign health.</div>;

  const health = data?.health;
  const adv = data?.adventure;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Campaign health</h1>
          <p className="text-sm text-zinc-400">
            {adv ? `${adv.name}${adv.place ? ` — ${adv.place}` : ''} · day ${health?.days ?? 0} · ${adv.committed} committed` : 'No active campaign'}
          </p>
        </div>
        <select
          className="rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-zinc-200"
          value={teamId}
          onChange={(e) => setSearchParams({ team_id: e.target.value })}
        >
          {(teams.data?.teams ?? []).map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </header>

      {adv && <CurrentStatusPanel adventureId={adv.id} />}

      {!health || !adv ? (
        <p className="text-sm text-zinc-400">No active campaign for this team. Health is measured against the promise, so there is nothing to measure yet.</p>
      ) : (
        <>
          {/* The sentence to read if you read nothing else. */}
          <div className="rounded-lg border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-sm text-amber-100">
            {health.headline}
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat
              label="In flight"
              value={String(health.flow.wip)}
              sub={health.flow.queue > 0 ? `${health.flow.completed} completed · ${health.flow.queue} queued` : `${health.flow.completed} completed`}
            />
            <Stat label="Throughput" value={`${health.flow.throughput_per_day}/day`} sub={`${health.flow.elapsed_days} days elapsed`} />
            <Stat
              label="Cycle time"
              value={health.flow.cycle_time_median_days === null ? '—' : `${health.flow.cycle_time_median_days}d`}
              sub={health.flow.cycle_time_days === null ? 'median, start to done' : `median · ${health.flow.cycle_time_days}d by WIP÷rate`}
            />
            <Stat
              label="Will carry over"
              value={health.flow.expected_carry_over === null ? '—' : String(Math.round(health.flow.expected_carry_over))}
              sub={health.flow.days_remaining === null ? undefined : `${health.flow.days_remaining}d left`}
            />
          </div>

          {/* The bathtub. Arrivals against departures. A campaign
              total says work started 46 and finished 49; only the
              shape over time says whether that is steady or two
              panicked days at either end. */}
          <section className="rounded-lg border border-white/10 bg-black/20 p-4">
            <h2 className="text-sm font-medium text-zinc-200">Arrivals vs departures</h2>
            <p className="mb-3 text-xs text-zinc-500">
              Per campaign day, scoped to the {adv.committed} committed tickets. Work arriving faster than it leaves means the pile grows, and the pile divided by the drain rate is the cycle time.
            </p>
            {(() => {
              const max = Math.max(1, ...health.daily_flow.map((d) => Math.max(d.arrivals, d.departures)));
              const h = (n: number) => (n / max) * 58;
              return (
                <div className="flex items-stretch gap-1" style={{ height: 130 }}>
                  {health.daily_flow.map((d) => (
                    <div
                      key={d.day}
                      className="flex flex-1 flex-col"
                      title={`Day ${d.day} · ${d.arrivals} arrived, ${d.departures} finished`}
                    >
                      <div className="flex flex-1 flex-col justify-end">
                        <div className="bg-sky-400/70" style={{ height: `${h(d.arrivals)}px` }} />
                      </div>
                      <div className="h-px bg-white/25" />
                      <div className="flex flex-1 flex-col">
                        <div className="bg-emerald-400/70" style={{ height: `${h(d.departures)}px` }} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-zinc-400">
              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-sky-400/70" />arrived</span>
              <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-400/70" />finished</span>
              {health.flow.rework_rate !== null && health.flow.work_started > 0 ? (
                <span className="text-zinc-500">
                  · rework: {health.flow.sent_back} of{' '}
                  {health.flow.work_started + health.flow.sent_back} arrivals into starting were sent back
                  {' '}({Math.round(health.flow.rework_rate * 100)}%)
                </span>
              ) : null}
            </div>
          </section>

          {/* Cumulative flow. The band that widens is where work is
              piling up — this is the one chart that shows trend,
              balance and arrivals at the same time. */}
          <section className="rounded-lg border border-white/10 bg-black/20 p-4">
            <h2 className="text-sm font-medium text-zinc-200">Cumulative flow</h2>
            <p className="mb-3 text-xs text-zinc-500">
              Where every committed ticket stood at the end of each campaign day. A band that widens is work collecting.
            </p>
            <div className="flex items-end gap-1" style={{ height: 180 }}>
              {health.cfd.map((d) => {
                const total = Math.max(1, adv.committed);
                return (
                  <div key={d.day} className="flex flex-1 flex-col justify-end" title={`Day ${d.day}`}>
                    {CFD_ORDER.map((p) =>
                      d.counts[p] > 0 ? (
                        <div
                          key={p}
                          className={PHASE_COLOUR[p]}
                          style={{ height: `${(d.counts[p] / total) * 170}px` }}
                          title={`Day ${d.day} · ${PHASE_LABEL[p]}: ${d.counts[p]}`}
                        />
                      ) : null,
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-zinc-400">
              {CFD_ORDER.map((p) => (
                <span key={p} className="flex items-center gap-1">
                  <span className={`inline-block h-2 w-2 rounded-sm ${PHASE_COLOUR[p]}`} />
                  {PHASE_LABEL[p]}
                </span>
              ))}
            </div>
          </section>

          {/* Dwell time. This is the bottleneck detector: the column
              that holds the most work for the longest is the
              constraint, whether or not anyone has called it blocked. */}
          <section className="rounded-lg border border-white/10 bg-black/20 p-4">
            <h2 className="text-sm font-medium text-zinc-200">Where work is sitting</h2>
            <p className="mb-3 text-xs text-zinc-500">
              Days in current status. Queued tickets have not been started — their age is waiting time, not work.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500">
                  <th className="py-1">Phase</th>
                  <th className="py-1 text-right">Tickets</th>
                  <th className="py-1 text-right">Median</th>
                  <th className="py-1 text-right">Oldest</th>
                </tr>
              </thead>
              <tbody>
                {health.dwell.map((d) => (
                  <tr key={d.phase} className="border-t border-white/5">
                    <td className="py-1.5">
                      <span className="flex items-center gap-2">
                        <span className={`inline-block h-2 w-2 rounded-sm ${PHASE_COLOUR[d.phase]}`} />
                        {PHASE_LABEL[d.phase]}
                      </span>
                    </td>
                    <td className="py-1.5 text-right font-medium text-zinc-200">{d.count}</td>
                    <td className="py-1.5 text-right text-zinc-300">{d.count ? `${d.median_days}d` : '—'}</td>
                    <td className="py-1.5 text-right text-zinc-400">
                      {d.oldest_key ? `${d.oldest_days}d · ${d.oldest_key}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Sprint over sprint. The honest place to ask whether the
              campaign is slowing down: thirteen daily points inside
              one fortnight is thin ground for a trend line, four
              sprints of the same measurements is not. */}
          {data.trend.length > 1 ? (
            <section className="rounded-lg border border-white/10 bg-black/20 p-4">
              <h2 className="text-sm font-medium text-zinc-200">Sprint over sprint</h2>
              <p className="mb-3 text-xs text-zinc-500">Oldest first. Each row is the whole sprint measured the same way.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500">
                    <th className="py-1">Sprint</th>
                    <th className="py-1 text-right">Committed</th>
                    <th className="py-1 text-right">Done</th>
                    <th className="py-1 text-right">Per day</th>
                    <th className="py-1 text-right">Cycle</th>
                    <th className="py-1 text-right">Rework</th>
                    <th className="py-1 text-right">Carried</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trend.map((s) => (
                    <tr key={s.id} className={`border-t border-white/5 ${s.status === 'active' ? 'text-zinc-100' : 'text-zinc-300'}`}>
                      <td className="py-1.5">
                        {s.name}
                        {s.status === 'active' ? <span className="ml-2 rounded bg-sky-400/20 px-1.5 py-0.5 text-[10px] text-sky-200">active</span> : null}
                      </td>
                      <td className="py-1.5 text-right">{s.committed}</td>
                      <td className="py-1.5 text-right">{s.completed}</td>
                      <td className="py-1.5 text-right">{s.throughput_per_day}</td>
                      <td className="py-1.5 text-right">{s.cycle_time_median_days === null ? '—' : `${s.cycle_time_median_days}d`}</td>
                      <td className="py-1.5 text-right">{s.rework_rate === null ? '—' : `${Math.round(s.rework_rate * 100)}%`}</td>
                      <td className="py-1.5 text-right">{s.status === 'active' ? `${s.wip} open` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {/* Load balance: is anyone carrying the sprint alone. */}
          <section className="rounded-lg border border-white/10 bg-black/20 p-4">
            <h2 className="text-sm font-medium text-zinc-200">Load across the team</h2>
            <p className="mb-3 text-xs text-zinc-500">
              In-flight tickets per person, so queued work is not counted as a load. Owner is the assignee, falling back to the most recent actor.
            </p>
            {health.people.length === 0 ? (
              <p className="text-sm text-zinc-400">Nothing in flight.</p>
            ) : (
              <div className="space-y-1.5">
                {health.people.map((p) => {
                  const max = Math.max(...health.people.map((x) => x.total), 1);
                  return (
                    <div key={p.name} className="flex items-center gap-3 text-sm">
                      <span className="w-40 shrink-0 truncate text-zinc-300">{p.name}</span>
                      <span className="flex h-3 flex-1 overflow-hidden rounded-full bg-white/5">
                        {(['in_progress', 'finishing', 'blocked'] as const).map((ph) =>
                          p[ph] > 0 ? (
                            <span key={ph} className={`h-full ${PHASE_COLOUR[ph]}`} style={{ width: `${(p[ph] / max) * 100}%` }} />
                          ) : null,
                        )}
                      </span>
                      <span className="w-8 shrink-0 text-right text-zinc-400">{p.total}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * The current status report, surfaced where the operator already looks.
 *
 * The full history lives on the adventure's own tab; this is the "where are we
 * right now" answer without three clicks, which is what was actually wanted.
 * Same engine, same stored reports — nothing separate to drift.
 */
function CurrentStatusPanel({ adventureId }: { adventureId: string }) {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ['battle-reports', adventureId],
    queryFn: async () => {
      const res = await api<{ reports: StoredReportLite[] }>(`/api/admin/adventures/${adventureId}/battle-reports`, {});
      return res.reports;
    },
  });
  const generate = useMutation({
    mutationFn: async () => {
      const res = await api<{ report: StoredReportLite }>(`/api/admin/adventures/${adventureId}/battle-reports`, { method: 'POST' });
      return res.report;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['battle-reports', adventureId] }),
  });

  const newest = reports.data?.[0];

  return (
    <section className="rounded border border-white/10 bg-black/30 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-zinc-200">Current status</h2>
          {newest ? (
            <>
              <p className={`mt-1 text-sm ${newest.verdict === 'won' || newest.verdict === 'complete' ? 'text-gold' : newest.verdict === 'at_risk' || newest.verdict === 'unrecoverable' ? 'text-accent-danger' : 'text-zinc-200'}`}>
                {newest.headline}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {new Date(newest.generated_at).toLocaleString()} · {newest.kind === 'daily' ? 'daily' : 'ad hoc'}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm text-zinc-400">No report yet. The first appears at 08:00, or generate one now.</p>
          )}
        </div>
        <button type="button" className="btn-primary shrink-0" onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Generating…' : 'Generate now'}
        </button>
      </div>
      {newest && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-zinc-400">Read the whole report</summary>
          <pre className="mt-2 whitespace-pre-wrap font-mono text-xs text-zinc-400">{newest.body}</pre>
        </details>
      )}
    </section>
  );
}
interface StoredReportLite { id: string; generated_at: string; kind: 'daily' | 'adhoc'; verdict: 'complete'|'won'|'drifting'|'at_risk'|'unrecoverable'; headline: string; body: string; }
